'use client'

import { useEffect, useMemo } from 'react'
import { Dialog, Button, Divider, Navigation, InfoBanner, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useFormContext } from 'react-hook-form'

import { v4 as uuid } from 'uuid'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'
import { sellerMakeOfferStartEvent } from '@marketplace-web/offers/price-offers-data'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { SERVICE_FEE_HELP_URL } from '../../constants/routes'
import OfferSubmitButton from '../OfferSubmitButton/OfferSubmitButton'
import { OfferPriceField } from '../fields/OfferPriceField'
import ItemSummary from '../ItemSummary'

import { OfferFormModel } from '../types'

type Props = {
  show: boolean
  minItemPrice?: CurrencyAmountModel
  maxItemPrice?: CurrencyAmountModel
  thresholdPrice: string | null
  isSubmitting: boolean
  itemPrice: CurrencyAmountModel
  onSubmit: (formValues: OfferFormModel, offerSessionId: string) => void
  onClose: () => void
  itemId: number
  itemTitle?: string
  itemThumbnailUrl?: string | null
  orderItemIds?: Array<number>
  transactionId?: number
}

const SellerOfferDialog = ({
  show,
  onClose,
  onSubmit,
  itemPrice,
  isSubmitting,
  minItemPrice,
  maxItemPrice,
  thresholdPrice,
  itemId,
  itemTitle,
  itemThumbnailUrl,
  orderItemIds,
  transactionId,
}: Props) => {
  const translate = useTranslate('conversation.hero_message.make_offer')
  const { track } = useTracking()
  const { handleSubmit } = useFormContext<OfferFormModel>()

  const thresholdBannerKey = 'offer_modal.threshold_banner_title.seller'
  const offerSessionId = useMemo(() => uuid(), [])

  function renderNavigation() {
    return (
      <Navigation
        body={translate('offer_modal.title')}
        right={
          <Button
            inline
            styling="flat"
            iconName={X24}
            testId="offer-modal-navigation-close-button"
            onClick={onClose}
          />
        }
        testId="offer-modal-navigation"
      />
    )
  }

  const handleFormSubmit = (formValues: OfferFormModel) => onSubmit(formValues, offerSessionId)

  useEffect(() => {
    if (!show) return

    track(
      sellerMakeOfferStartEvent({
        offerSessionId,
        hasSuggestedPrices: false,
        currencyCode: itemPrice.currencyCode,
        currentPrice: Number(parseFloat(itemPrice.amount).toFixed(2)),
        transactionId: transactionId?.toString(),
      }),
    )
  }, [itemPrice.amount, itemPrice.currencyCode, offerSessionId, track, show, transactionId])

  return (
    <Dialog show={show} testId="offer-modal">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {renderNavigation()}
        <Divider />
        {thresholdPrice && (
          <InfoBanner
            body={
              <Text
                as="span"
                text={translate(thresholdBannerKey, {
                  amount: thresholdPrice,
                  'learn-more': chunks => (
                    <a href={SERVICE_FEE_HELP_URL} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                })}
                html
              />
            }
            type="info"
          />
        )}
        <ItemSummary
          price={itemPrice}
          title={itemTitle}
          thumbnailUrl={itemThumbnailUrl}
          itemCount={orderItemIds?.length}
        />
        <OfferPriceField
          minItemPrice={minItemPrice}
          maxItemPrice={maxItemPrice}
          currentItemPrice={itemPrice}
          itemId={itemId}
          orderItemIds={orderItemIds}
        />
        <OfferSubmitButton
          currency={itemPrice.currencyCode}
          isSubmitting={isSubmitting}
          transactionId={transactionId}
        />
      </form>
    </Dialog>
  )
}

export default SellerOfferDialog
